/* Модель синтеза озвучки в чате (05.11.2026, тимлид и владелец): STEOS Voice ² — новый синтез (по умолчанию), STEOS Voice ¹ — прежний.
   Короткое нажатие на голос в списке выбирает его на v2. В подвале карточки голоса (правки Леонида 05.11.2026): ♥, «Выбрать голос» в две
   строки — снизу мельче «Активная модель: STEOS Voice ²», строка-пояснение над кнопками остаётся; справа круглая кнопка «v2». Она открывает
   нижний лист «Модель синтеза» с двумя плитками, как лист фильтров (openFilterSheet): у каждой модели — что она умеет (со слов владельца:
   v2 — 19 языков, естественное звучание, качественные клоны; эмоции и настройки v2 пока не включены — в плитку не пишем). Номер модели — бейдж-капсула справа сверху по брендбуку «Энсо STEOS»
   («Бейдж версии»: только номер, без буквы v). Выбранная модель уходит вместе с голосом (PUT /app/tts/voice, tts_engine).
   У своих клонов, у выбора голоса для экранов студии и у кнопки подборки кнопки модели нет — только v2. */
import { accountLocale, uiFormat, uiText } from '../../i18n';
import type { TtsEngine } from '../../api/types';
import { openFilterSheet, type FilterGroup } from '../../ui/filter-sheet';
import './tts-engine.css';

const esc = (s: string) => s.replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
/** Номер поколения модели для бейджа: v2 → «2», v1 → «1» */
const gen = (e: TtsEngine) => e.slice(1);
/** Бейдж версии по брендбуку: капсула после направления, в ней только номер (вид — мягкий, как метки Mini App, tts-engine.css) */
export const versionBadge = (e: TtsEngine) => `<sup class="vbadge">${gen(e)}</sup>`;

/** Языки модели STEOS Voice ² (список владельца 05.11.2026; армянский — первым, владелец); по-русски — запасные названия, если в браузере нет Intl.DisplayNames */
export const V2_LANG_LIST: ReadonlyArray<readonly [string, string]> = [
  ['hy', 'Армянский'], ['ru', 'Русский'], ['en', 'Английский'], ['de', 'Немецкий'], ['fr', 'Французский'], ['es', 'Испанский'], ['it', 'Итальянский'], ['pt', 'Португальский'],
  ['pl', 'Польский'], ['cs', 'Чешский'], ['ro', 'Румынский'], ['hu', 'Венгерский'], ['el', 'Греческий'], ['tr', 'Турецкий'], ['zh', 'Китайский'],
  ['ja', 'Японский'], ['ko', 'Корейский'], ['ar', 'Арабский'], ['hi', 'Хинди'],
];
/** Сколько языков у модели STEOS Voice ²: вкладка «+N» плеера карточки голоса и плитка листа моделей */
export const V2_LANGS = V2_LANG_LIST.length;
/** Пояснение к «Голос говорит на 19 языках» в плеере карточки: языки — у модели STEOS Voice ² (Леонид: должно быть понятно, что это для v2).
    «этих языков» — мини-таблетка, открывает лист со списком языков плитками (владелец 05.11.2026) */
export const voiceLangsNote = () =>
  esc(uiFormat('На модели STEOS Voice {0} все голоса мультиязычные и озвучат текст на любом из {1}.', '\u0001', '\u0002'))
    .replace('\u0001', versionBadge('v2'))
    .replace('\u0002', `<button type="button" class="vl-pill" data-v2-langs aria-haspopup="dialog">${esc(uiText('этих языков'))}<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 6 6 6-6 6"/></svg></button>`);

/** Название языка на языке интерфейса (с прописной), иначе — по-русски из списка */
function langName(code: string, ru: string) {
  try {
    const n = new Intl.DisplayNames([accountLocale()], { type: 'language' }).of(code);
    if (n && n !== code) return n.charAt(0).toLocaleUpperCase(accountLocale()) + n.slice(1);
  } catch { /* старый браузер — русское название */ }
  return ru;
}
const X = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>';
type LegacySheet = { html(): string; open(html: string): void; el(): HTMLElement | null };
let langSheet: LegacySheet | null = null;
/** Лист «Языки STEOS Voice ²»: плитки в две колонки — код языка в кружке и название, как плитки листа фильтров; слоем поверх карточки голоса */
function openLangSheet() {
  const BC = (window as unknown as { BotCore?: { sheet(o: { id: string; label: string; layer?: boolean; onClose(): void }): LegacySheet } }).BotCore;
  if (!BC) return;
  const title = uiText('Языки озвучки');
  if (!langSheet) langSheet = BC.sheet({ id: 'bcV2Langs', label: title, layer: true, onClose: () => {} });
  if (!document.getElementById('bcV2Langs')) (document.getElementById('bot') || document.body).insertAdjacentHTML('beforeend', langSheet.html());
  const tiles = V2_LANG_LIST.map(([c, ru]) => `<li class="vl-t"><span class="vl-c">${c.toUpperCase()}</span><b>${esc(langName(c, ru))}</b></li>`).join('');
  const sub = esc(uiFormat('Любой голос на модели STEOS Voice {0} озвучит текст на каждом из этих языков', '\u0001')).replace('\u0001', versionBadge('v2'));
  langSheet.open(`<div class="cbody fs-sh vl-sh"><div class="shead"><div class="m"><h2>${esc(title)}</h2><p>${sub}</p></div><button class="iconb xbtn" type="button" data-bsheet-x aria-label="${esc(uiText('Закрыть'))}">${X}</button></div><ul class="vl-grid" aria-label="${esc(title)}">${tiles}</ul><div class="cta"><button class="btn" type="button" data-bsheet-x>${esc(uiText('Готово'))}</button></div></div>`);
}
/* таблетка «этих языков» в плеере карточки: перехват на погружении, чтобы нажатие не переключало плеер и не закрывало карточку */
document.addEventListener('click', e => {
  const t = e.target instanceof Element ? e.target.closest('[data-v2-langs]') : null;
  if (!t) return;
  e.stopPropagation();
  e.preventDefault();
  openLangSheet();
}, true);

/** Модель, выбранная в открытой карточке; уходит вместе с голосом по «Выбрать голос» */
let current: TtsEngine = 'v2';
let shown = false;
let root: HTMLElement | null = null;

/** Тег выбранного голоса в списке (вид .tag.eng — без значка клона): только у прежней модели — «Voice ¹»; v2 — обычный синтез, без метки */
export const engineTag = (engine: TtsEngine | undefined) => (engine === 'v1' ? { t: 'Voice', b: gen('v1'), c: 'eng' } : '');

/** Модель, с которой «Выбрать голос» выберет голос открытой карточки */
export const cardEngine = (): TtsEngine => (shown ? current : 'v2');
/** Подписчик смены модели карточки: плеер «Пример звучания» показывает «+N» и «Голос говорит на 19 языках» только у STEOS Voice ² */
let listener: (() => void) | null = null;
export const onCardEngine = (fn: () => void) => { listener = fn; };
const changed = (prev: TtsEngine) => { if (cardEngine() !== prev) listener?.(); };

/** Лок-ап модели внутри плитки листа, как в брендбуке «Энсо STEOS» («Шёлк и имя»): слово STEOS — тот же знак-маска, что в боковой
    панели (assets/steos-wordmark.png), направление «Voice» светлым тоном Onest той же толщины, бейдж версии справа сверху — капсула тушью */
const lockup = (e: TtsEngine) => {
  const asset = (window as unknown as { ASSET?: (f: string) => string }).ASSET?.('steos-wordmark.png') ?? 'assets/steos-wordmark.png';
  return `<span class="eng-lu" aria-hidden="true"><i class="wm" style="--wm:url(${asset})"></i><span class="dir">Voice</span><span class="bb">${gen(e)}</span></span>`;
};

const groups = (): FilterGroup[] => [{
  key: 'engine', label: uiText('Модель для озвучки в чате'), def: ['v2'], value: [current],
  items: [
    { id: 'v2', label: 'STEOS Voice 2', lockup: lockup('v2'), sub: uiText('Новая модель: 19 языков, естественное звучание с живыми интонациями — без металла и роботизированности, качественное клонирование голоса') },
    { id: 'v1', label: 'STEOS Voice 1', lockup: lockup('v1'), sub: uiText('Прежняя модель: только 5 языков, без эмоций и вопросительной интонации, без клонов голоса') },
  ],
}];

/** Кнопки подвала под выбранную модель: вторая строка «Выбрать голос» и круглая кнопка справа */
function paint() {
  if (!root) return;
  const pick = root.querySelector<HTMLElement>('#sPick'), b = root.querySelector<HTMLElement>('#sEng');
  if (pick && shown) pick.innerHTML = `<span class="eng-l"><b>${esc(uiText('Выбрать голос'))}</b><small><span class="t">${esc(uiText('Активная модель:'))}</span><span class="n">STEOS Voice</span>${versionBadge(current)}</small></span>`;
  if (b) b.textContent = current;
}

function openSheet() {
  openFilterSheet({
    groups,
    t: { title: uiText('Модель синтеза'), close: uiText('Закрыть'), reset: uiText('Сбросить'), done: uiText('Готово') },
    onChange: (_key, value) => { const prev = cardEngine(); current = value[0] === 'v1' ? 'v1' : 'v2'; paint(); changed(prev); },
  });
}

/** Кнопка модели в подвале карточки (.oacts с #sPick): voiceId — голос карточки; show — показывать ли; engine — модель, с которой
    карточка открылась (у голоса, выбранного сейчас для чата, — его модель, у остальных — v2). Вызывается при каждой отрисовке карточки
    после подписи #sPick: пока открыт тот же голос, выбранная в листе модель стоит */
export function syncEngineSwitch(oacts: HTMLElement | null, voiceId: number, show: boolean, engine: TtsEngine) {
  if (!oacts || !oacts.querySelector('#sPick')) return;
  root = oacts;
  let b = oacts.querySelector<HTMLButtonElement>('#sEng');
  if (!b) {
    oacts.insertAdjacentHTML('beforeend', `<button class="rb eng-b" type="button" id="sEng" aria-haspopup="dialog" aria-label="${esc(uiText('Модель синтеза'))}"></button>`);
    b = oacts.querySelector<HTMLButtonElement>('#sEng')!;
    b.addEventListener('click', e => { e.stopPropagation(); openSheet(); });
  }
  const prev = cardEngine();
  if (!show || !shown || oacts.dataset.engVoice !== String(voiceId)) current = engine;
  oacts.dataset.engVoice = String(voiceId);
  shown = show;
  b.hidden = !show;
  oacts.classList.toggle('eng-on', show);
  paint();
  changed(prev);
}
